import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Repeat, CheckCircle2, AlertCircle, Loader2, RotateCcw, Package, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const STATUS_COLORS = {
  PENDING_FOLDING: 'bg-amber-100 text-amber-700',
  FOLDED:          'bg-emerald-100 text-emerald-700',
  PRESSING:        'bg-sky-100 text-sky-700',
  QTY_CONFIRMATION:'bg-indigo-100 text-indigo-700',
  PROCESSED:       'bg-green-200 text-green-900',
};

const fmt = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const ReFoldingEntry = () => {
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [scan, setScan] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookup, setLookup] = useState(null);   // {source, current_section, usn_count, ...}
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [recent, setRecent] = useState([]);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { loadRecent(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const loadRecent = async () => {
    try {
      const r = await apiClient.get('/inhouse/re-folding/recent', { params: { limit: 20 } });
      setRecent(r.data?.rows || []);
    } catch {
      // ignore — non-blocking
    }
  };

  const runLookup = async () => {
    const code = scan.trim();
    if (code.length < 2) { toast.error('Enter at least 2 characters'); return; }
    setLookupLoading(true);
    setError('');
    setLookup(null);
    try {
      const r = await apiClient.get('/inhouse/re-folding/lookup', { params: { bundle_code: code } });
      setLookup(r.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Lookup failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setLookupLoading(false);
    }
  };

  const onScanKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); runLookup(); }
  };

  const submit = async () => {
    if (!lookup?.bundle_code) return;
    setSubmitting(true);
    try {
      const r = await apiClient.post('/inhouse/re-folding/create', {
        bundle_code: lookup.bundle_code,
        reason: reason.trim() || null,
      });
      toast.success(r.data?.message || 'Bundle marked for Re-Folding');
      // Reset for next scan
      setScan('');
      setLookup(null);
      setReason('');
      setError('');
      inputRef.current?.focus();
      loadRecent();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to mark for Re-Folding';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setScan(''); setLookup(null); setReason(''); setError('');
    inputRef.current?.focus();
  };

  const existing = lookup?.existing_inhouse_record;
  const alreadyInFlow = existing && ['PENDING_FOLDING','FOLDED','PRESSING','QTY_CONFIRMATION'].includes((existing.status || '').toUpperCase());

  return (
    <div className="min-h-screen bg-gray-50" data-testid="re-folding-entry-page">
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate('/sections/inhouse')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex items-center gap-2">
          <Repeat className="w-5 h-5 text-rose-600" />
          <div>
            <h1 className="text-lg font-bold text-gray-900">Re-Folding Entry</h1>
            <p className="text-xs text-gray-500">Scan an existing bundle to send it back through Folding (skips Processing Inward)</p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 space-y-4">
        {/* Scan card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Search className="w-4 h-4 text-gray-500" />
              Scan Bundle Code
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={scan}
                onChange={(e) => setScan(e.target.value)}
                onKeyDown={onScanKeyDown}
                placeholder="e.g. BUN012345/26-27"
                className="font-mono text-base"
                disabled={lookupLoading || submitting}
                data-testid="bundle-scan-input"
                autoFocus
              />
              <Button
                onClick={runLookup}
                disabled={lookupLoading || submitting || scan.trim().length < 2}
                data-testid="lookup-btn"
              >
                {lookupLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Looking up</>
                ) : (
                  <><Search className="w-4 h-4 mr-2" /> Lookup</>
                )}
              </Button>
              {(lookup || error) && (
                <Button variant="outline" onClick={reset} data-testid="reset-btn">
                  <RotateCcw className="w-4 h-4" />
                </Button>
              )}
            </div>
            {error && (
              <Alert className="bg-red-50 border-red-200">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-700 text-sm" data-testid="lookup-error">{error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Lookup result */}
        {lookup && (
          <Card className="border-rose-200" data-testid="lookup-result">
            <CardHeader className="pb-2 bg-rose-50/50">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Package className="w-4 h-4 text-rose-600" />
                  Bundle Details
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">
                    Source: {lookup.current_section}
                  </Badge>
                  <Badge variant="outline" className="text-[10px] font-mono">
                    {lookup.source}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-xs text-gray-500">Bundle Code</p>
                  <p className="font-mono font-semibold">{lookup.bundle_code}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">USNs</p>
                  <p className="font-bold text-rose-700 text-lg" data-testid="lookup-usn-count">{lookup.usn_count}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Design</p>
                  <p className="font-medium">{lookup.design_name || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Shade</p>
                  <p className="font-medium">{lookup.shade || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Sort No</p>
                  <p className="font-medium">{lookup.sort_no || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">SKU</p>
                  <p className="font-mono text-xs">{lookup.sku_code || '-'}</p>
                </div>
                {lookup.current_status && (
                  <div>
                    <p className="text-xs text-gray-500">Current Status (source)</p>
                    <p className="font-medium text-xs">{lookup.current_status}</p>
                  </div>
                )}
              </div>

              {existing && (
                <Alert className={alreadyInFlow ? "bg-amber-50 border-amber-300" : "bg-blue-50 border-blue-200"}>
                  <AlertCircle className={`h-4 w-4 ${alreadyInFlow ? 'text-amber-600' : 'text-blue-600'}`} />
                  <AlertDescription className="text-sm">
                    <span className="font-medium">Existing Inhouse record:</span>{' '}
                    flow_type=<code>{existing.flow_type}</code>, status=<code>{existing.status}</code>
                    {existing.created_at && <> (created {fmt(existing.created_at)})</>}.
                    {alreadyInFlow && existing.flow_type === 'RE_FOLDING' && (
                      <div className="mt-1 text-amber-800">
                        ⚠️ This bundle is already marked for Re-Folding and is mid-flow.
                        Submitting will be blocked.
                      </div>
                    )}
                    {!alreadyInFlow && (
                      <div className="mt-1 text-blue-800">
                        Submitting will reset this row to PENDING_FOLDING with flow_type=RE_FOLDING.
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {lookup.usn_count === 0 && (
                <Alert className="bg-red-50 border-red-200">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-sm text-red-700">
                    This bundle has no minted USNs in {lookup.source}. Re-Folding
                    requires pre-minted USNs — use Bundle Receipt Entry for fresh bundles.
                  </AlertDescription>
                </Alert>
              )}

              {/* Reason (optional) */}
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">
                  Reason for Re-Folding <span className="text-gray-400">(optional)</span>
                </label>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. damaged fold, customer return, mis-shade…"
                  data-testid="reason-input"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-1">
                <Button variant="outline" onClick={reset} disabled={submitting} data-testid="cancel-btn">
                  Cancel
                </Button>
                <Button
                  onClick={submit}
                  disabled={submitting || lookup.usn_count === 0 || (alreadyInFlow && existing?.flow_type === 'RE_FOLDING')}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="confirm-refolding-btn"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Marking…</>
                  ) : (
                    <><CheckCircle2 className="w-4 h-4 mr-2" /> Mark for Re-Folding</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent */}
        {recent.length > 0 && (
          <Card data-testid="recent-refoldings-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <History className="w-4 h-4 text-gray-500" />
                Recent Re-Folding Entries ({recent.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bundle</TableHead>
                    <TableHead>Design / Shade</TableHead>
                    <TableHead>Sort</TableHead>
                    <TableHead className="text-center">USNs</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>Marked</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recent.map((r) => (
                    <TableRow key={r.bundle_code} data-testid={`recent-row-${r.bundle_code}`}>
                      <TableCell className="font-mono text-xs">{r.bundle_code}</TableCell>
                      <TableCell className="text-xs">
                        <div className="font-medium">{r.design_name || '-'}</div>
                        <div className="text-gray-500">{r.shade || ''}</div>
                      </TableCell>
                      <TableCell className="text-xs">{r.sort_no || '-'}</TableCell>
                      <TableCell className="text-center font-semibold">{r.usn_count || 0}</TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_COLORS[r.status] || 'bg-gray-100 text-gray-700'} border-0 text-[10px]`}>
                          {r.status || '-'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{r.refolding_source_section || '-'}</TableCell>
                      <TableCell className="text-xs">
                        <div>{fmt(r.refolding_marked_at)}</div>
                        {r.refolding_marked_by_name && (
                          <div className="text-gray-400 text-[10px]">{r.refolding_marked_by_name}</div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ReFoldingEntry;
